import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Badge, Button, Form, FormControl, Spinner, Table } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import { css, StyleSheet } from 'aphrodite';
import {
  faArrowLeft,
  faCheck,
  faEdit,
  faExternalLinkAlt,
  faFileExport,
  faLayerGroup,
  faSort,
  faSortDown,
  faSortUp,
  faTimes,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import { useDebounce } from 'use-debounce';

import {
  areYouSure,
  confirmRichAction,
  exportFileErrorSwal,
  exportFileSuccessSwal,
  SwalToast,
} from 'lib/utils/toast';
import {
  backgroundGrey,
  blackberry,
  bootstrapDanger,
  darkGray,
  disabledGray,
  grape,
  greyBorder,
  lightIndigo,
  lightPurple,
  lightPurple2,
  mutedGray,
  softAmberLight,
  standardGray,
  white,
} from 'lib/css/colors';
import HierarchyBreadcrumb from 'components/l4_segmentation/HierarchyBreadcrumb';
import {
  L4_CATEGORY_DIVISION_EXPORT_RESEGMENTATION_ERROR_REPORT,
  L4_CATEGORY_DIVISION_EXPORT_RESEGMENTATION_VIEW,
  L4_CATEGORY_DIVISION_RESEGMENTED_BULK_MOVE_CLUSTER,
  L4_CATEGORY_DIVISION_RESEGMENTED_BULK_REMOVE,
  L4_CATEGORY_DIVISION_RESEGMENTED_CLASSIFICATION_OPTIONS,
  L4_CATEGORY_DIVISION_RESEGMENTED_CLASSIFY,
  L4_CATEGORY_DIVISION_RESEGMENTED_CLUSTER_NAMES,
  L4_CATEGORY_DIVISION_RESEGMENTED_FILTER_OPTIONS,
  L4_CATEGORY_DIVISION_RESEGMENTED_MOVE_CLUSTER,
  L4_CATEGORY_DIVISION_RESEGMENTED_PRODUCTS,
  L4_CATEGORY_DIVISION_RESEGMENTED_REMOVE_PRODUCT,
  L4_CATEGORY_DIVISION_RESEGMENTED_RENAME_CLUSTER,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import { ProductType, ProductTypeDisplay } from 'lib/enums/ProductType';
import ResegmentedClassificationModal from 'components/l4_segmentation/ResegmentedClassificationModal';
import { ResegmentedProductSortField as SORT_FIELDS } from 'lib/enums/ResegmentedProductSortField';
import useGet from 'lib/hooks/useGet';
import useL4SegmentationUrlParams from 'lib/hooks/useL4SegmentationUrlParams';
import usePost from 'lib/hooks/usePost';

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;

const clusterRowSelectStyles = {
  container: provided => ({ ...provided, minWidth: 140, maxWidth: 220 }),
  control: (provided, state) => ({
    ...provided,
    fontSize: '12px',
    borderColor: state.isFocused ? grape : greyBorder,
    boxShadow: state.isFocused ? `0 0 0 2px ${lightIndigo}` : 'none',
    borderRadius: '6px',
    minHeight: '32px',
    ':hover': { borderColor: grape },
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '12px',
    backgroundColor: state.isSelected ? blackberry : state.isFocused ? lightPurple2 : white,
    color: state.isSelected ? white : darkGray,
  }),
  singleValue: provided => ({ ...provided, fontSize: '12px', color: darkGray }),
  placeholder: provided => ({ ...provided, fontSize: '12px', color: mutedGray }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
};

const selectStyles = {
  container: provided => ({ ...provided, minWidth: 220 }),
  control: (provided, state) => ({
    ...provided,
    fontSize: '13px',
    borderColor: state.isFocused ? grape : greyBorder,
    boxShadow: state.isFocused ? `0 0 0 2px ${lightIndigo}` : 'none',
    borderRadius: '6px',
    minHeight: '34px',
    ':hover': { borderColor: grape },
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '13px',
    backgroundColor: state.isSelected ? blackberry : state.isFocused ? lightPurple2 : white,
    color: state.isSelected ? white : darkGray,
  }),
  singleValue: provided => ({ ...provided, fontSize: '13px', color: darkGray }),
  placeholder: provided => ({ ...provided, fontSize: '13px', color: mutedGray }),
};

const bulkClusterSelectStyles = {
  container: provided => ({ ...provided, minWidth: 200, maxWidth: 280 }),
  control: (provided, state) => ({
    ...provided,
    fontSize: '13px',
    borderColor: state.isFocused ? grape : greyBorder,
    boxShadow: state.isFocused ? `0 0 0 2px ${lightIndigo}` : 'none',
    borderRadius: '6px',
    minHeight: '32px',
    ':hover': { borderColor: grape },
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '13px',
    backgroundColor: state.isSelected ? blackberry : state.isFocused ? lightPurple2 : white,
    color: state.isSelected ? white : darkGray,
  }),
  singleValue: provided => ({ ...provided, fontSize: '13px', color: darkGray }),
  placeholder: provided => ({ ...provided, fontSize: '13px', color: mutedGray }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
};

function ResegmentedProductsView({ category, onBack }) {
  const { params, replaceParams } = useL4SegmentationUrlParams();
  const [currentPage, setCurrentPage] = useState(() => params.page);
  const [itemsPerPage, setItemsPerPage] = useState(() => params.perPage || DEFAULT_PAGE_SIZE);
  const [selectedCluster, setSelectedCluster] = useState(() =>
    params.cluster ? { label: params.cluster, value: params.cluster } : null,
  );
  const [selectedBrand, setSelectedBrand] = useState(() =>
    params.brand ? { label: params.brand, value: params.brand } : null,
  );
  const [selectedEntity, setSelectedEntity] = useState(() =>
    params.entity ? { label: params.entity, value: params.entity } : null,
  );
  const [searchTerm, setSearchTerm] = useState(() => params.q || '');
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const [globalProductIdTerm, setGlobalProductIdTerm] = useState(
    () => params.globalProductId || '',
  );
  const [debouncedGlobalProductIdTerm] = useDebounce(globalProductIdTerm, SEARCH_DEBOUNCE_MS);
  const [pendingRowId, setPendingRowId] = useState(null);
  const [showClassifyModal, setShowClassifyModal] = useState(false);
  const [modalClassificationOptions, setModalClassificationOptions] = useState([]);
  const [selectedClassificationClusters, setSelectedClassificationClusters] = useState([]);
  const [classificationClassNames, setClassificationClassNames] = useState({});
  const [removeUnselectedClassificationClusters, setRemoveUnselectedClassificationClusters] =
    useState(false);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkClusterOption, setBulkClusterOption] = useState(null);
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);
  const [isBulkMoving, setIsBulkMoving] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Rename cluster state
  const [isRenamingCluster, setIsRenamingCluster] = useState(false);
  const [renameClusterValue, setRenameClusterValue] = useState('');

  const categoryIdRef = useRef(null);

  const { postData: postExportResegmentationView, loading: exportingResegmentationView } = usePost(
    L4_CATEGORY_DIVISION_EXPORT_RESEGMENTATION_VIEW,
    () =>
      exportFileSuccessSwal(
        'The re-segmentation table export has been queued. The CSV file will be generated in a while.',
      ),
    err =>
      exportFileErrorSwal(
        err?.response?.data?.error || 'An error occurred while trying to export the file',
      ),
  );

  const {
    postData: postExportResegmentationErrorReport,
    loading: exportingResegmentationErrorReport,
  } = usePost(
    L4_CATEGORY_DIVISION_EXPORT_RESEGMENTATION_ERROR_REPORT,
    () =>
      exportFileSuccessSwal(
        'The cluster conflict error report has been queued. The CSV file will be generated in a while.',
      ),
    err =>
      exportFileErrorSwal(
        err?.response?.data?.error || 'An error occurred while trying to export the error report',
      ),
  );

  const { postData: postMoveCluster, loading: movingResegmentedCluster } = usePost(
    L4_CATEGORY_DIVISION_RESEGMENTED_MOVE_CLUSTER,
  );
  const { postData: postRemoveResegmented, loading: removingResegmentedProduct } = usePost(
    L4_CATEGORY_DIVISION_RESEGMENTED_REMOVE_PRODUCT,
  );
  const { postData: postBulkMoveCluster } = usePost(
    L4_CATEGORY_DIVISION_RESEGMENTED_BULK_MOVE_CLUSTER,
  );
  const { postData: postBulkRemove } = usePost(L4_CATEGORY_DIVISION_RESEGMENTED_BULK_REMOVE);
  const { postData: postRenameCluster, loading: isSubmittingRename } = usePost(
    L4_CATEGORY_DIVISION_RESEGMENTED_RENAME_CLUSTER,
    res => {
      const newName = res?.data?.data?.new_cluster_name;
      SwalToast('success', res?.data?.data?.message || `Cluster renamed.`);
      if (newName) setSelectedCluster({ label: newName, value: newName });
      refetchClusterNames();
      refetchProducts();
    },
    err => {
      SwalToast('error', err?.response?.data?.error || 'Failed to rename cluster.');
    },
  );
  const { postData: postClassifyResegmented, loading: classifyingResegmented } = usePost(
    L4_CATEGORY_DIVISION_RESEGMENTED_CLASSIFY,
  );

  const { data: { data: clusterNames = [] } = {}, refetch: refetchClusterNames } = useGet(
    L4_CATEGORY_DIVISION_RESEGMENTED_CLUSTER_NAMES,
    { l4_category_id: category.id },
  );
  const { loading: loadingClassificationOptions, refetch: refetchClassificationOptions } = useGet(
    L4_CATEGORY_DIVISION_RESEGMENTED_CLASSIFICATION_OPTIONS,
    { l4_category_id: category.id },
    false,
    true,
  );

  const clusterOptions = useMemo(
    () => clusterNames.map(name => ({ label: name, value: name })),
    [clusterNames],
  );

  const searchQuery = (params.q || '').trim();
  const globalProductIdQuery = (params.globalProductId || '').trim();
  const filterOptionParams = {
    l4_category_id: category.id,
    ...(selectedCluster ? { cluster_name: selectedCluster.value } : {}),
    ...(searchQuery ? { q: searchQuery } : {}),
    ...(selectedBrand ? { brand_name: selectedBrand.value } : {}),
    ...(selectedEntity ? { entity_name: selectedEntity.value } : {}),
  };
  const {
    data: { data: { brands = [], entities = [] } = {} } = {},
    refetch: refetchFilterOptions,
  } = useGet(L4_CATEGORY_DIVISION_RESEGMENTED_FILTER_OPTIONS, filterOptionParams);

  const brandOptions = useMemo(() => brands.map(name => ({ label: name, value: name })), [brands]);

  const entityOptions = useMemo(
    () => entities.map(name => ({ label: name, value: name })),
    [entities],
  );

  const hasMissingClassificationClass = useMemo(
    () =>
      selectedClassificationClusters.some(cluster => !classificationClassNames[cluster]?.trim()),
    [classificationClassNames, selectedClassificationClusters],
  );

  useEffect(() => {
    setCurrentPage(params.page);
    if (params.perPage) setItemsPerPage(params.perPage);
  }, [params.page, params.perPage]);

  useEffect(() => {
    setSearchTerm(params.q || '');
  }, [params.q]);

  useEffect(() => {
    setGlobalProductIdTerm(params.globalProductId || '');
  }, [params.globalProductId]);

  const initializeClassificationOptions = useCallback(options => {
    setModalClassificationOptions(options);
    setSelectedClassificationClusters(
      options.map(option => option.cluster_name).filter(clusterName => Boolean(clusterName)),
    );
    setClassificationClassNames(
      options.reduce((acc, option) => {
        if (option.cluster_name) {
          acc[option.cluster_name] = option.class_name || '';
        }
        return acc;
      }, {}),
    );
  }, []);

  useEffect(() => {
    const nextQuery = debouncedSearchTerm.trim();
    if (nextQuery !== (params.q || '')) {
      setCurrentPage(1);
      replaceParams({ q: nextQuery || null, page: null });
    }
  }, [debouncedSearchTerm, params.q, replaceParams]);

  useEffect(() => {
    const nextId = debouncedGlobalProductIdTerm.trim();
    if (nextId !== (params.globalProductId || '')) {
      setCurrentPage(1);
      replaceParams({ globalProductId: nextId || null, page: null });
    }
  }, [debouncedGlobalProductIdTerm, params.globalProductId, replaceParams]);

  useEffect(() => {
    if (!params.brand) {
      setSelectedBrand(null);
      return;
    }
    const match = brandOptions.find(o => o.value === params.brand);
    setSelectedBrand(match || { label: params.brand, value: params.brand });
  }, [params.brand, brandOptions]);

  useEffect(() => {
    if (!params.entity) {
      setSelectedEntity(null);
      return;
    }
    const match = entityOptions.find(o => o.value === params.entity);
    setSelectedEntity(match || { label: params.entity, value: params.entity });
  }, [params.entity, entityOptions]);

  useEffect(() => {
    if (!params.cluster) {
      setSelectedCluster(null);
      return;
    }
    const match = clusterOptions.find(o => o.value === params.cluster);
    setSelectedCluster(match || { label: params.cluster, value: params.cluster });
  }, [params.cluster, clusterOptions]);

  useEffect(() => {
    if (categoryIdRef.current === null) {
      categoryIdRef.current = category.id;
      return;
    }
    if (categoryIdRef.current !== category.id) {
      categoryIdRef.current = category.id;
      setCurrentPage(1);
      setSelectedCluster(null);
      setSelectedBrand(null);
      setSelectedEntity(null);
      setSearchTerm('');
      setGlobalProductIdTerm('');
      setSelectedIds(new Set());
      setSortField(null);
      replaceParams({
        cluster: null,
        brand: null,
        entity: null,
        globalProductId: null,
        page: null,
        perPage: null,
        q: null,
      });
    }
  }, [category.id, replaceParams]);

  const offset = (currentPage - 1) * itemsPerPage;

  const {
    data: {
      data: products = [],
      total_count: totalCount = 0,
      category_hierarchy: categoryHierarchy = null,
    } = {},
    loading,
    error,
    refetch: refetchProducts,
  } = useGet(L4_CATEGORY_DIVISION_RESEGMENTED_PRODUCTS, {
    l4_category_id: category.id,
    limit: itemsPerPage,
    offset,
    ...(selectedCluster ? { cluster_name: selectedCluster.value } : {}),
    ...(searchQuery ? { q: searchQuery } : {}),
    ...(selectedBrand ? { brand_name: selectedBrand.value } : {}),
    ...(selectedEntity ? { entity_name: selectedEntity.value } : {}),
    ...(globalProductIdQuery ? { global_product_id: globalProductIdQuery } : {}),
    ...(sortField ? { sort_field: sortField, sort_dir: sortDir } : {}),
  });

  // Selection helpers
  const selectableIds = useMemo(() => products.filter(p => p.id).map(p => p.id), [products]);
  const allOnPageSelected =
    selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));
  const someOnPageSelected = selectableIds.some(id => selectedIds.has(id));
  const selectedCount = selectedIds.size;

  const buildRowClusterOptions = useCallback(
    currentName => {
      const names = new Set(clusterNames);
      if (currentName) names.add(currentName);
      return [...names].sort().map(n => ({ label: n, value: n }));
    },
    [clusterNames],
  );

  const refreshResegmentationTable = useCallback(() => {
    refetchProducts();
    refetchClusterNames();
    refetchFilterOptions();
    refetchClassificationOptions();
  }, [refetchProducts, refetchClusterNames, refetchFilterOptions, refetchClassificationOptions]);

  // --- Sort handler ---
  const handleSort = field => {
    setSelectedIds(new Set());
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
    replaceParams({ page: null });
  };

  const getSortIcon = field => {
    if (sortField !== field) return faSort;
    return sortDir === 'asc' ? faSortUp : faSortDown;
  };

  // --- Rename cluster handlers ---
  const handleStartRenameCluster = () => {
    setRenameClusterValue(selectedCluster?.value || '');
    setIsRenamingCluster(true);
  };

  const handleCancelRenameCluster = () => {
    setIsRenamingCluster(false);
    setRenameClusterValue('');
  };

  const runRenameCluster = trimmed => {
    postRenameCluster({
      l4_category_id: category.id,
      old_cluster_name: selectedCluster.value,
      new_cluster_name: trimmed,
    });
  };

  const handleSubmitRenameCluster = () => {
    const trimmed = renameClusterValue.trim();
    if (!trimmed || trimmed === selectedCluster?.value) {
      handleCancelRenameCluster();
      return;
    }
    const html =
      `Rename cluster <b>${selectedCluster.value}</b> to <b>${trimmed}</b>` +
      ' for all products in this category?';
    confirmRichAction(() => runRenameCluster(trimmed), html, 'Rename cluster?', 'Yes, rename');
    // close the inline input immediately so the modal has focus
    setIsRenamingCluster(false);
    setRenameClusterValue('');
  };

  // --- Selection handlers ---
  const handleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        selectableIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        selectableIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const handleToggleSelect = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setBulkClusterOption(null);
  };

  // --- Bulk delete (single task endpoint) ---
  // eslint-disable-next-line space-before-function-paren
  const runBulkRemove = async () => {
    const ids = [...selectedIds];
    setIsBulkRemoving(true);
    try {
      const res = await postBulkRemove({
        l4_category_id: category.id,
        division_result_ids: ids,
      });
      if (!res || res.status !== 200 || res.data?.ok === false) {
        SwalToast(
          'error',
          res?.response?.data?.message ||
            res?.data?.error ||
            res?.message ||
            'Bulk remove task failed.',
        );
        return;
      }
      SwalToast('success', res.data?.data?.message || `${ids.length} product(s) removed.`);
      setSelectedIds(new Set());
      refreshResegmentationTable();
    } finally {
      setIsBulkRemoving(false);
    }
  };

  const handleBulkRemove = () => {
    areYouSure(
      runBulkRemove,
      `${selectedCount} selected product(s) will be queued for removal.`,
      'Remove selected from results?',
    );
  };

  // --- Bulk move cluster (single task endpoint) ---
  // eslint-disable-next-line space-before-function-paren
  const handleBulkMoveCluster = async () => {
    if (!bulkClusterOption?.value?.trim()) return;
    const newCluster = bulkClusterOption.value.trim();
    const ids = [...selectedIds];
    setIsBulkMoving(true);
    try {
      const res = await postBulkMoveCluster({
        l4_category_id: category.id,
        division_result_ids: ids,
        new_cluster_name: newCluster,
      });
      if (!res || res.status !== 200 || res.data?.ok === false) {
        SwalToast(
          'error',
          res?.response?.data?.message ||
            res?.data?.error ||
            res?.message ||
            'Bulk move cluster task failed.',
        );
        return;
      }
      SwalToast('success', res.data?.data?.message || `${ids.length} product(s) moved.`);
      setBulkClusterOption(null);
      setSelectedIds(new Set());
      refreshResegmentationTable();
    } finally {
      setIsBulkMoving(false);
    }
  };

  // --- Per-row handlers ---
  const handleClusterRowChange = (product, option) => {
    const next = option?.value?.trim();
    if (!next || next === product.openai_cluster_name) return;
    if (!product.id) {
      SwalToast('error', 'Cannot update cluster for this row.');
      return;
    }
    setPendingRowId(product.id);
    postMoveCluster({
      l4_category_id: category.id,
      division_result_id: product.id,
      new_cluster_name: next,
    })
      .then(res => {
        if (!res || res.status !== 200 || res.data?.ok === false) {
          SwalToast(
            'error',
            res?.response?.data?.error ||
              res?.data?.error ||
              res?.message ||
              'Could not update cluster.',
          );
          return;
        }
        SwalToast('success', 'Cluster updated.');
        refreshResegmentationTable();
      })
      .finally(() => setPendingRowId(null));
  };

  const runRemoveProduct = async product => {
    if (!product.id) {
      SwalToast('error', 'Cannot remove this row.');
      return;
    }
    setPendingRowId(product.id);
    try {
      const res = await postRemoveResegmented({
        l4_category_id: category.id,
        division_result_id: product.id,
      });
      if (!res || res.status !== 200 || res.data?.ok === false) {
        SwalToast(
          'error',
          res?.response?.data?.error ||
            res?.data?.error ||
            res?.message ||
            'Could not remove product.',
        );
        return;
      }
      SwalToast('success', 'Product removed from re-segmentation results.');
      refreshResegmentationTable();
    } finally {
      setPendingRowId(null);
    }
  };

  const handleRemoveProduct = product => {
    areYouSure(
      () => runRemoveProduct(product),
      'This product will be excluded from results. No cluster applies.',
      'Remove from results?',
    );
  };

  const handlePageSizeChange = newSize => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
    setSelectedIds(new Set());
    replaceParams({
      perPage: newSize === DEFAULT_PAGE_SIZE ? null : newSize,
      page: null,
    });
  };

  const handlePageChange = page => {
    setCurrentPage(page);
    setSelectedIds(new Set());
    replaceParams({ page: page <= 1 ? null : page });
  };

  const handleClusterChange = option => {
    setSelectedCluster(option);
    setCurrentPage(1);
    setSelectedIds(new Set());
    replaceParams({ cluster: option?.value ?? null, page: null });
  };

  const handleBrandChange = option => {
    setSelectedBrand(option);
    setCurrentPage(1);
    setSelectedIds(new Set());
    replaceParams({ brand: option?.value ?? null, page: null });
  };

  const handleEntityChange = option => {
    setSelectedEntity(option);
    setCurrentPage(1);
    setSelectedIds(new Set());
    replaceParams({ entity: option?.value ?? null, page: null });
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };

  const handleGlobalProductIdChange = e => {
    const raw = e.target.value;
    if (raw === '' || /^\d+$/.test(raw)) {
      setGlobalProductIdTerm(raw);
    }
  };

  const handleExportTable = () => {
    postExportResegmentationView({
      l4_category_id: category.id,
      l4_category_name: category.category_name,
      ...(selectedCluster ? { cluster_name: selectedCluster.value } : {}),
    });
  };

  const handleExportErrorReport = () => {
    postExportResegmentationErrorReport({
      l4_category_id: category.id,
      l4_category_name: category.category_name,
    });
  };

  const handleOpenClassifyModal = () => {
    setModalClassificationOptions([]);
    setSelectedClassificationClusters([]);
    setClassificationClassNames({});
    setRemoveUnselectedClassificationClusters(false);
    setShowClassifyModal(true);
    refetchClassificationOptions(res => {
      initializeClassificationOptions(res?.data?.data || []);
    });
  };

  const handleCloseClassifyModal = () => {
    if (classifyingResegmented) return;
    setShowClassifyModal(false);
  };

  const handleClassificationClusterToggle = clusterName => {
    setSelectedClassificationClusters(current => {
      if (current.includes(clusterName)) {
        return current.filter(name => name !== clusterName);
      }
      return [...current, clusterName];
    });
  };

  const handleClassificationClassNameChange = (clusterName, className) => {
    setClassificationClassNames(current => ({
      ...current,
      [clusterName]: className,
    }));
  };

  const handleSubmitClassification = () => {
    if (hasMissingClassificationClass) {
      SwalToast('error', 'All clusters must have a class name before classification.');
      return;
    }
    if (selectedClassificationClusters.length === 0) {
      SwalToast('error', 'Select at least one cluster to classify.');
      return;
    }

    postClassifyResegmented({
      l4_category_id: category.id,
      selected_cluster_names: selectedClassificationClusters,
      cluster_class_names: classificationClassNames,
      remove_unselected_clusters: removeUnselectedClassificationClusters,
    }).then(res => {
      if (!res || res.status !== 200 || res.data?.ok === false) {
        SwalToast(
          'error',
          res?.response?.data?.message ||
            res?.response?.data?.error ||
            res?.data?.error ||
            res?.message ||
            'Could not classify re-segmented products.',
        );
        return;
      }
      const warning = res.data?.data?.cleanup_warning;
      const message = res.data?.data?.message;
      SwalToast(
        warning ? 'warning' : 'success',
        warning || message || 'Re-segmented products classified successfully.',
      );
      setShowClassifyModal(false);
      refreshResegmentationTable();
      refetchClassificationOptions();
    });
  };

  const isBulkBusy = isBulkRemoving || isBulkMoving;

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <Button className={css(styles.backButton)} onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Categories
        </Button>
        <h4 className="mb-0 font-weight-bold flex-grow-1">{category.category_name}</h4>
        <div className={css(styles.headerRight)}>
          <Button
            variant="primary"
            size="sm"
            onClick={handleOpenClassifyModal}
            disabled={loading || totalCount === 0}
            className={css(styles.exportBtn)}
            aria-label="Classify re-segmented clusters"
          >
            Classify
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleExportTable}
            disabled={exportingResegmentationView}
            className={css(styles.exportBtn)}
            aria-label="Export current table to CSV"
          >
            {exportingResegmentationView ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <>
                <FontAwesomeIcon icon={faFileExport} className="mr-1" />
                Export table
              </>
            )}
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleExportErrorReport}
            disabled={exportingResegmentationErrorReport || loading || totalCount === 0}
            className={css(styles.exportBtn)}
            aria-label="Export cluster conflict error report to CSV"
            title="Export products with the same Global Product ID assigned to different clusters"
          >
            {exportingResegmentationErrorReport ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <>
                <FontAwesomeIcon icon={faFileExport} className="mr-1" />
                Error report
              </>
            )}
          </Button>
          <span className={css(styles.countBadge)}>{totalCount.toLocaleString()} products</span>
        </div>
      </div>

      <div className={css(styles.contentArea)}>
        <HierarchyBreadcrumb
          hierarchy={categoryHierarchy}
          targetAttribute={products[0]?.target_attribute_type || null}
        />

        <div className={css(styles.searchToolbar)}>
          <div className={css(styles.filterControl)}>
            <span className={css(styles.filterLabel)}>Cluster Name</span>
            {isRenamingCluster ? (
              <div className={css(styles.renameClusterRow)}>
                <FormControl
                  value={renameClusterValue}
                  onChange={e => setRenameClusterValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSubmitRenameCluster();
                    if (e.key === 'Escape') handleCancelRenameCluster();
                  }}
                  maxLength={255}
                  autoFocus
                  aria-label="New cluster name"
                  className={css(styles.renameClusterInput)}
                />
                <Button
                  variant="link"
                  onClick={handleSubmitRenameCluster}
                  disabled={isSubmittingRename}
                  aria-label="Save cluster rename"
                  className={css(styles.renameActionBtn)}
                >
                  {isSubmittingRename ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faCheck} />
                  )}
                </Button>
                <Button
                  variant="link"
                  onClick={handleCancelRenameCluster}
                  aria-label="Cancel cluster rename"
                  className={css(styles.renameActionBtn)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </div>
            ) : (
              <div className={css(styles.clusterFilterRow)}>
                <div className={css(styles.clusterSelectWrapper)}>
                  <Select
                    options={clusterOptions}
                    value={selectedCluster}
                    onChange={handleClusterChange}
                    placeholder="All"
                    isClearable
                    styles={selectStyles}
                  />
                </div>
                {selectedCluster && (
                  <Button
                    variant="link"
                    onClick={handleStartRenameCluster}
                    aria-label="Rename this cluster"
                    className={css(styles.renameActionBtn)}
                    title="Rename cluster for all products"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className={css(styles.filterControl)}>
            <span className={css(styles.filterLabel)}>Name / SKU</span>
            <FormControl
              value={searchTerm}
              onChange={handleSearchChange}
              maxLength={128}
              placeholder="Search product name or SKU"
              className={css(styles.searchInput)}
              aria-label="Search re-segmented products by name or SKU"
            />
          </div>
          <div className={css(styles.filterControl)}>
            <span className={css(styles.filterLabel)}>Brand</span>
            <Select
              options={brandOptions}
              value={selectedBrand}
              onChange={handleBrandChange}
              placeholder="All"
              isClearable
              styles={selectStyles}
            />
          </div>
          <div className={css(styles.filterControl)}>
            <span className={css(styles.filterLabel)}>Mfr / Vendor</span>
            <Select
              options={entityOptions}
              value={selectedEntity}
              onChange={handleEntityChange}
              placeholder="All"
              isClearable
              styles={selectStyles}
            />
          </div>
          <div className={css(styles.filterControl, styles.filterControlNarrow)}>
            <span className={css(styles.filterLabel)}>Global Product ID</span>
            <FormControl
              value={globalProductIdTerm}
              onChange={handleGlobalProductIdChange}
              maxLength={20}
              placeholder="e.g. 1899399"
              className={css(styles.searchInput, styles.searchInputNarrow)}
              aria-label="Filter by Global Product ID"
              inputMode="numeric"
            />
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className={css(styles.errorState)}>
            Failed to load re-segmentation results. Please try again later.
          </div>
        ) : products.length === 0 ? (
          <div className={css(styles.emptyState)}>
            {searchQuery
              ? 'No products match this search.'
              : 'No re-segmentation results found for this category.'}
          </div>
        ) : (
          <>
            {/* Bulk action bar */}
            {selectedCount > 0 && (
              <div className={css(styles.bulkBar)} role="toolbar" aria-label="Bulk actions">
                <span className={css(styles.bulkCount)}>{selectedCount} selected</span>
                <Button
                  variant="link"
                  size="sm"
                  className={css(styles.bulkClearBtn)}
                  onClick={handleClearSelection}
                  disabled={isBulkBusy}
                  aria-label="Clear selection"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  Clear
                </Button>
                <div className={css(styles.bulkDivider)} />
                <div className={css(styles.bulkMoveGroup)}>
                  <CreatableSelect
                    aria-label="Move selected to cluster"
                    options={clusterOptions}
                    value={bulkClusterOption}
                    onChange={setBulkClusterOption}
                    onCreateOption={inputValue =>
                      setBulkClusterOption({ label: inputValue, value: inputValue })
                    }
                    placeholder="Move to cluster..."
                    isClearable
                    isDisabled={isBulkBusy}
                    styles={bulkClusterSelectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    formatCreateLabel={val => `Create cluster "${val}"`}
                  />
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleBulkMoveCluster}
                    disabled={!bulkClusterOption || isBulkBusy}
                    aria-label="Apply cluster to selected products"
                  >
                    {isBulkMoving ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faLayerGroup} className="mr-1" />
                        Apply
                      </>
                    )}
                  </Button>
                </div>
                <div className={css(styles.bulkDivider)} />
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleBulkRemove}
                  disabled={isBulkBusy}
                  aria-label="Remove selected products from results"
                >
                  {isBulkRemoving ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrashAlt} className="mr-1" />
                      Delete selected
                    </>
                  )}
                </Button>
              </div>
            )}

            <div className={css(styles.tableWrapper)}>
              <Table hover className={css(styles.table)}>
                <thead className={css(styles.tableHead)}>
                  <tr>
                    <th className={css(styles.tableHeadCell, styles.checkboxCell)}>
                      <Form.Check
                        type="checkbox"
                        checked={allOnPageSelected}
                        ref={el => {
                          if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected;
                        }}
                        onChange={handleSelectAll}
                        aria-label="Select all on page"
                      />
                    </th>
                    <th
                      className={css(styles.tableHeadCell, styles.sortableHeadCell)}
                      style={{ width: '17%' }}
                      onClick={() => handleSort(SORT_FIELDS.PRODUCT_NAME)}
                    >
                      Product Name
                      <FontAwesomeIcon
                        icon={getSortIcon(SORT_FIELDS.PRODUCT_NAME)}
                        className={css(
                          styles.sortIcon,
                          sortField === SORT_FIELDS.PRODUCT_NAME && styles.sortIconActive,
                        )}
                      />
                    </th>
                    <th
                      className={css(styles.tableHeadCell, styles.sortableHeadCell)}
                      style={{ width: '9%' }}
                      onClick={() => handleSort(SORT_FIELDS.SKU)}
                    >
                      Mfr/Vendor SKU
                      <FontAwesomeIcon
                        icon={getSortIcon(SORT_FIELDS.SKU)}
                        className={css(
                          styles.sortIcon,
                          sortField === SORT_FIELDS.SKU && styles.sortIconActive,
                        )}
                      />
                    </th>
                    <th
                      className={css(styles.tableHeadCell, styles.sortableHeadCell)}
                      style={{ width: '7%' }}
                      onClick={() => handleSort(SORT_FIELDS.GLOBAL_PRODUCT_ID)}
                    >
                      Global Product ID
                      <FontAwesomeIcon
                        icon={getSortIcon(SORT_FIELDS.GLOBAL_PRODUCT_ID)}
                        className={css(
                          styles.sortIcon,
                          sortField === SORT_FIELDS.GLOBAL_PRODUCT_ID && styles.sortIconActive,
                        )}
                      />
                    </th>
                    <th
                      className={css(styles.tableHeadCell, styles.sortableHeadCell)}
                      style={{ width: '10%' }}
                      onClick={() => handleSort(SORT_FIELDS.BRAND_NAME)}
                    >
                      Brand
                      <FontAwesomeIcon
                        icon={getSortIcon(SORT_FIELDS.BRAND_NAME)}
                        className={css(
                          styles.sortIcon,
                          sortField === SORT_FIELDS.BRAND_NAME && styles.sortIconActive,
                        )}
                      />
                    </th>
                    <th
                      className={css(styles.tableHeadCell, styles.sortableHeadCell)}
                      style={{ width: '13%' }}
                      onClick={() => handleSort(SORT_FIELDS.ENTITY_NAME)}
                    >
                      Mfr / Vendor
                      <FontAwesomeIcon
                        icon={getSortIcon(SORT_FIELDS.ENTITY_NAME)}
                        className={css(
                          styles.sortIcon,
                          sortField === SORT_FIELDS.ENTITY_NAME && styles.sortIconActive,
                        )}
                      />
                    </th>
                    <th
                      className={css(styles.tableHeadCell, styles.sortableHeadCell)}
                      style={{ width: '7%' }}
                      onClick={() => handleSort(SORT_FIELDS.PRODUCT_TYPE)}
                    >
                      Type
                      <FontAwesomeIcon
                        icon={getSortIcon(SORT_FIELDS.PRODUCT_TYPE)}
                        className={css(
                          styles.sortIcon,
                          sortField === SORT_FIELDS.PRODUCT_TYPE && styles.sortIconActive,
                        )}
                      />
                    </th>
                    <th
                      className={css(styles.tableHeadCell, styles.sortableHeadCell)}
                      style={{ width: '13%' }}
                      onClick={() => handleSort(SORT_FIELDS.CLUSTER_NAME)}
                    >
                      Cluster Name
                      <FontAwesomeIcon
                        icon={getSortIcon(SORT_FIELDS.CLUSTER_NAME)}
                        className={css(
                          styles.sortIcon,
                          sortField === SORT_FIELDS.CLUSTER_NAME && styles.sortIconActive,
                        )}
                      />
                    </th>
                    <th
                      className={css(styles.tableHeadCell, styles.sortableHeadCell)}
                      style={{ width: '8%' }}
                      onClick={() => handleSort(SORT_FIELDS.CLASS_NAME)}
                    >
                      Class Name
                      <FontAwesomeIcon
                        icon={getSortIcon(SORT_FIELDS.CLASS_NAME)}
                        className={css(
                          styles.sortIcon,
                          sortField === SORT_FIELDS.CLASS_NAME && styles.sortIconActive,
                        )}
                      />
                    </th>
                    <th className={css(styles.tableHeadCell)} style={{ width: '6%' }}>
                      Actions
                    </th>
                    <th className={css(styles.tableHeadCell)} style={{ width: '3%' }}>
                      View
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => (
                    <tr
                      key={product.id || index}
                      className={
                        product.id && selectedIds.has(product.id)
                          ? css(styles.selectedRow)
                          : undefined
                      }
                    >
                      <td className={css(styles.checkboxCell)}>
                        {product.id ? (
                          <Form.Check
                            type="checkbox"
                            checked={selectedIds.has(product.id)}
                            onChange={() => handleToggleSelect(product.id)}
                            aria-label={`Select ${product.product_name || 'product'}`}
                          />
                        ) : null}
                      </td>
                      <td>
                        <span className={css(styles.productNameText)}>
                          {product.product_name || <span className={css(styles.na)}>N/A</span>}
                        </span>
                      </td>
                      <td>
                        <span className={css(styles.cellText)}>
                          {product.sku || <span className={css(styles.na)}>N/A</span>}
                        </span>
                      </td>
                      <td>
                        <span className={css(styles.cellText)}>
                          {product.global_product_id || <span className={css(styles.na)}>N/A</span>}
                        </span>
                      </td>
                      <td>
                        <span className={css(styles.brandName)}>
                          {product.brand_name || <span className={css(styles.na)}>Unknown</span>}
                        </span>
                      </td>
                      <td>
                        <span className={css(styles.cellText)}>
                          {product.entity_name || <span className={css(styles.na)}>N/A</span>}
                        </span>
                      </td>
                      <td className="text-center">
                        <Badge
                          variant={
                            product.product_type === ProductType.MANUFACTURER ? 'info' : 'secondary'
                          }
                        >
                          {product.product_type === ProductType.MANUFACTURER
                            ? ProductTypeDisplay.MANUFACTURER
                            : ProductTypeDisplay.DISTRIBUTOR}
                        </Badge>
                      </td>
                      <td className={css(styles.clusterCell)}>
                        <CreatableSelect
                          aria-label="Change cluster"
                          options={buildRowClusterOptions(product.openai_cluster_name)}
                          value={
                            product.openai_cluster_name
                              ? {
                                  label: product.openai_cluster_name,
                                  value: product.openai_cluster_name,
                                }
                              : null
                          }
                          onChange={opt => handleClusterRowChange(product, opt)}
                          onCreateOption={inputValue =>
                            handleClusterRowChange(product, { value: inputValue })
                          }
                          placeholder="Select cluster"
                          isClearable={false}
                          isDisabled={pendingRowId === product.id || loading}
                          isLoading={pendingRowId === product.id && movingResegmentedCluster}
                          styles={clusterRowSelectStyles}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          menuPosition="fixed"
                          formatCreateLabel={inputValue => `Create cluster "${inputValue}"`}
                        />
                      </td>
                      <td>
                        <span className={css(styles.cellText)}>
                          {product.openai_class_name || <span className={css(styles.na)}>—</span>}
                        </span>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="link"
                          size="sm"
                          className={css(styles.removeBtn)}
                          disabled={pendingRowId === product.id || loading || !product.id}
                          onClick={() => handleRemoveProduct(product)}
                          aria-label="Remove from re-segmentation results"
                          title="Remove from results (cluster not applicable)"
                        >
                          {pendingRowId === product.id && removingResegmentedProduct ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FontAwesomeIcon icon={faTrashAlt} />
                          )}
                        </Button>
                      </td>
                      <td className="text-center">
                        {product.global_product_id && product.product_id && product.product_type ? (
                          <a
                            href={`/global-product-detail/${product.global_product_id}/${product.product_id}/${product.product_type}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={css(styles.productLink)}
                            aria-label="View product"
                          >
                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                          </a>
                        ) : (
                          <span className={css(styles.na)}>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {totalCount > itemsPerPage && (
              <div className={css(styles.paginationWrapper)}>
                <PaginationComponent
                  totalItems={totalCount}
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={handlePageSizeChange}
                  currentPage={currentPage}
                  setCurrentPage={handlePageChange}
                  disabled={loading}
                  defaultSize
                />
              </div>
            )}
          </>
        )}
      </div>
      <ResegmentedClassificationModal
        show={showClassifyModal}
        options={modalClassificationOptions}
        selectedClusters={selectedClassificationClusters}
        classNamesByCluster={classificationClassNames}
        removeUnselectedClusters={removeUnselectedClassificationClusters}
        loadingOptions={loadingClassificationOptions}
        submitting={classifyingResegmented}
        hasMissingClassName={hasMissingClassificationClass}
        onClose={handleCloseClassifyModal}
        onToggleCluster={handleClassificationClusterToggle}
        onClassNameChange={handleClassificationClassNameChange}
        onRemoveUnselectedClustersChange={setRemoveUnselectedClassificationClusters}
        onSubmit={handleSubmitClassification}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 'calc(100vh - 200px)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    borderBottom: `1px solid ${greyBorder}`,
    marginBottom: '0',
    flexShrink: 0,
  },
  backButton: {
    backgroundColor: 'transparent',
    borderColor: blackberry,
    color: blackberry,
    fontWeight: 500,
    marginRight: '12px',
    ':hover': {
      backgroundColor: blackberry,
      borderColor: blackberry,
      color: white,
    },
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    flexShrink: 0,
  },
  exportBtn: {
    fontWeight: 500,
  },
  countBadge: {
    fontSize: '13px',
    fontWeight: 600,
    color: white,
    backgroundColor: blackberry,
    padding: '5px 14px',
    borderRadius: '12px',
    flexShrink: 0,
  },
  contentArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '0 16px',
    overflow: 'hidden',
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: darkGray,
    whiteSpace: 'nowrap',
  },
  searchToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    gap: '10px',
    paddingBottom: '10px',
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  filterControl: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '220px',
    zIndex: 999,
  },
  filterControlNarrow: {
    minWidth: '140px',
    maxWidth: '160px',
  },
  searchInput: {
    minWidth: '260px',
    fontSize: '13px',
  },
  searchInputNarrow: {
    minWidth: '0',
    width: '100%',
  },
  clusterFilterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  clusterSelectWrapper: {
    flex: 1,
  },
  renameClusterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  renameClusterInput: {
    flex: 1,
    fontSize: '13px',
    minWidth: '160px',
  },
  renameActionBtn: {
    padding: '2px 6px',
    color: grape,
    ':hover': {
      color: blackberry,
    },
  },
  // Bulk action bar
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    backgroundColor: softAmberLight,
    border: `1px solid #fde68a`,
    borderRadius: '6px',
    marginBottom: '8px',
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  bulkCount: {
    fontSize: '13px',
    fontWeight: 600,
    color: darkGray,
    whiteSpace: 'nowrap',
  },
  bulkClearBtn: {
    color: mutedGray,
    padding: '0 6px',
    fontSize: '13px',
    ':hover': { color: bootstrapDanger },
  },
  bulkDivider: {
    width: '1px',
    height: '22px',
    backgroundColor: greyBorder,
    flexShrink: 0,
  },
  bulkMoveGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    zIndex: 1000,
  },
  // Table
  tableWrapper: {
    flex: 1,
    overflowY: 'auto',
    marginBottom: '16px',
  },
  table: {
    marginBottom: 0,
  },
  tableHead: {
    backgroundColor: backgroundGrey,
  },
  tableHeadCell: {
    position: 'sticky',
    top: 0,
    backgroundColor: backgroundGrey,
    zIndex: 2,
    boxShadow: `0 1px 0 ${greyBorder}`,
    verticalAlign: 'middle',
  },
  sortableHeadCell: {
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    ':hover': {
      backgroundColor: greyBorder,
    },
  },
  sortIcon: {
    marginLeft: '6px',
    fontSize: '11px',
    color: mutedGray,
    opacity: 0.5,
  },
  sortIconActive: {
    color: grape,
    opacity: 1,
  },
  checkboxCell: {
    width: '36px',
    textAlign: 'center',
    paddingLeft: '12px',
    paddingRight: '6px',
  },
  selectedRow: {
    backgroundColor: '#f0edff',
  },
  cellText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '220px',
    fontSize: '13px',
  },
  productNameText: {
    display: 'block',
    fontSize: '13px',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  brandName: {
    fontSize: '13px',
    fontWeight: 600,
    color: darkGray,
  },
  clusterCell: {
    verticalAlign: 'middle',
    maxWidth: '240px',
  },
  removeBtn: {
    color: mutedGray,
    padding: '2px 8px',
    ':hover': {
      color: bootstrapDanger,
    },
  },
  na: {
    color: disabledGray,
    fontStyle: 'italic',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    color: standardGray,
    fontSize: '14px',
  },
  errorState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    color: standardGray,
    fontSize: '14px',
  },
  productLink: {
    color: blackberry,
    fontSize: '14px',
    ':hover': {
      color: lightPurple,
    },
  },
  paginationWrapper: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '16px',
    borderTop: `1px solid ${greyBorder}`,
    flexShrink: 0,
  },
});

ResegmentedProductsView.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.number.isRequired,
    category_name: PropTypes.string.isRequired,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
};

export default ResegmentedProductsView;
